import { CSSProperties } from 'react';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  NodeKey,
  Spread,
} from 'lexical';
import { $applyNodeReplacement } from 'lexical';
import { LinkAttributes, LinkNode, SerializedLinkNode } from '@lexical/link';
import { isVariableToken } from '../editLink/EditLinkPlugin.utils';
import {
  convertCssPropertiesToCssText,
  convertStyleToCssProperties,
} from './lexicalNodes.utils';

interface AnchorPayload {
  attributes?: LinkAttributes;
  className?: string;
  style?: CSSProperties;
  key?: NodeKey;
  url: string;
}

export type SerializedAnchorNode = Spread<
  {
    className?: string;
    style?: CSSProperties;
  },
  SerializedLinkNode
>;

export class LinkStyledNode extends LinkNode {
  className: string;

  style: CSSProperties;

  constructor(
    url: string,
    attributes: LinkAttributes = {},
    className?: string,
    style?: CSSProperties,
    key?: NodeKey,
  ) {
    super(url, attributes, key);
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    this.className = className;
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    this.style = style;
  }

  static getType(): string {
    return 'link-styled';
  }

  static clone(node: LinkStyledNode): LinkStyledNode {
    const {
      __rel: rel,
      __target: target,
      __title: title,
      __url: url,
      __key: key,
    } = node;

    return new LinkStyledNode(
      url,
      { rel, target, title },
      node.className,
      node.style,
      key,
    );
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (_node: Node) => ({
        conversion: convertAnchorElement,
        priority: 2,
      }),
    };
  }

  static importJSON(serializedNode: SerializedAnchorNode): LinkStyledNode {
    const { className, rel, style, target, title, url } = serializedNode;
    return $createAnchorNode({
      attributes: { rel, target, title },
      className,
      style,
      url,
    });
  }

  sanitizeUrl(url: string): string {
    if (isVariableToken(url)) {
      return url;
    }
    return super.sanitizeUrl(url);
  }

  createDOM(config: EditorConfig): HTMLAnchorElement | HTMLSpanElement {
    const dom = super.createDOM(config);

    if (this.className) {
      dom.setAttribute('class', this.className);
    }

    if (this.style) {
      dom.setAttribute('style', convertCssPropertiesToCssText(this.style));
    }
    return dom;
  }

  exportJSON(): SerializedAnchorNode {
    return {
      ...super.exportJSON(),
      className: this.className,
      style: this.style,
      // We need to export the serialized anchor node with the customized type,
      // otherwise issues can arise from the type mismatch between lexical's
      // built-in LinkNode and this LinkStyledNode.
      type: this.getType(),
    };
  }
}

export function $createAnchorNode({
  attributes,
  className,
  key,
  style,
  url,
}: AnchorPayload): LinkStyledNode {
  return $applyNodeReplacement(
    new LinkStyledNode(url, attributes, className, style, key),
  );
}

function convertAnchorElement(domNode: Node): null | DOMConversionOutput {
  if (domNode instanceof HTMLAnchorElement) {
    const { className, rel, style, target, title } = domNode;

    // Use getAttribute('href') instead of the .href property to preserve the
    // raw attribute value. The .href property returns a browser-resolved absolute
    // URL, which corrupts template variable placeholders like [$VAR$] by
    // prepending the current page's base URL.
    const rawHref = domNode.getAttribute('href') ?? '';

    const node = $createAnchorNode({
      attributes: { rel, target, title },
      className,
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      style: convertStyleToCssProperties(style),
      url: rawHref,
    });
    return { node };
  }
  return null;
}
